import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/**
 * Single-input combobox: pick from master OR type a new value and create it inline.
 *
 *   items         [{ id, label, sub? }]  — master records
 *   value         currently selected id (string)
 *   placeholder   placeholder text when no value
 *   onChange      (id, item) called after pick OR after create
 *   onCreate      async (label) => newItem ({id,label,...})  — called when user adds new
 *   displayLabel  optional fallback label when value not in items (e.g. manual name)
 *   testIdPrefix  optional
 */
export default function MasterCombobox({ items = [], value = "", placeholder = "Select…", onChange, onCreate, displayLabel = "", testIdPrefix = "mc" }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [creating, setCreating] = useState(false);

    const selected = items.find((x) => x.id === value);
    const display = selected?.label || displayLabel || placeholder;
    const queryNorm = query.trim().toLowerCase();
    const exactMatch = items.some((i) => (i.label || "").toLowerCase() === queryNorm);

    const handleCreate = async () => {
        if (!queryNorm || !onCreate) return;
        setCreating(true);
        try {
            const newItem = await onCreate(query.trim());
            if (newItem) {
                onChange?.(newItem.id, newItem);
                toast.success(`Added "${newItem.label}" to master`);
                setOpen(false);
                setQuery("");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add to master");
        } finally {
            setCreating(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    className="rounded-none h-8 w-full justify-between font-normal text-left"
                    data-testid={`${testIdPrefix}-trigger`}
                    title={selected?.label || displayLabel || ""}
                >
                    <span className={cn("truncate", !selected && !displayLabel && "text-zinc-400")}>{display}</span>
                    <ChevronsUpDown size={12} className="ml-2 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 rounded-none" style={{ minWidth: "var(--radix-popover-trigger-width)", width: "max-content", maxWidth: "min(640px, 90vw)" }}>
                <Command shouldFilter>
                    <CommandInput
                        placeholder="Search or type to add…"
                        value={query}
                        onValueChange={setQuery}
                        data-testid={`${testIdPrefix}-search`}
                    />
                    <CommandList className="max-h-80">
                        <CommandEmpty>
                            {onCreate ? (
                                <button
                                    className="w-full text-left text-xs px-3 py-2 hover:bg-zinc-100 flex items-center gap-2"
                                    onClick={handleCreate}
                                    disabled={creating || !queryNorm}
                                    data-testid={`${testIdPrefix}-create-empty`}
                                >
                                    <Plus size={12} /> {creating ? "Adding…" : `Add "${query}" to master`}
                                </button>
                            ) : (
                                <span className="text-xs text-zinc-400 italic px-3 py-2">No matches</span>
                            )}
                        </CommandEmpty>
                        {onCreate && queryNorm && !exactMatch && (
                            <CommandGroup heading="New">
                                <CommandItem
                                    onSelect={handleCreate}
                                    className="text-[#002FA7]"
                                    data-testid={`${testIdPrefix}-create`}
                                >
                                    <Plus size={12} className="mr-2" /> Add "{query}" to master
                                </CommandItem>
                            </CommandGroup>
                        )}
                        <CommandGroup>
                            {items.map((it) => (
                                <CommandItem
                                    key={it.id}
                                    value={`${it.label} ${it.sub || ""}`}
                                    onSelect={() => {
                                        onChange?.(it.id, it);
                                        setOpen(false);
                                        setQuery("");
                                    }}
                                    data-testid={`${testIdPrefix}-item-${it.id}`}
                                    className="items-start"
                                >
                                    <Check size={12} className={cn("mr-2 mt-1 shrink-0", value === it.id ? "opacity-100" : "opacity-0")} />
                                    <div className="flex-1 whitespace-normal break-words leading-snug">{it.label}</div>
                                    {it.sub && <span className="text-[10px] text-zinc-500 ml-2 whitespace-nowrap shrink-0 self-center">{it.sub}</span>}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
